import { ChartSubType, GridDashboardChartItem, HistoricalUtrs, InsightDashboardItemType } from '../../../../../types/insight-custom-dashboard';
import { SingleValue } from './SingleValue';
import { PieChart } from '../PieChart';
import { LineChart } from '../LineChart';
import { ColumnChart } from '../ColumnChart';
import { BarChart } from '../BarChart';
import { Table } from './Table';
import { SurveyActionData } from '@g17eco/types/surveyAction';
import { ReactGoogleChartProps } from 'react-google-charts/dist/types';
import { ChartUtrData } from '../../types';
import { getBarChartData, getColumnChartData, getLineChartData, getPieChartData } from './data-utils';
import { CurrencyConfig, EMPTY_CURRENCY_CONFIG, getActualUtrvs, getLatestActualUtrvs } from './utils';
import { getNANRMessage } from '../common/getNANRMessage';
import { NANRMessage } from '../common/NANRMessage';
import { TakenFromText } from '../common/TakenFromText';
import { FULL_DATA_NA_NR_SUBTYPES, NA_NR_CHART_SUBTYPES } from '../common/naNrSubtypes';

interface ChartWidgetProps {
  readOnly: boolean;
  utrsData: ChartUtrData[];
  item: Pick<GridDashboardChartItem, 'subType' | 'variables' | 'calculation' | 'unitText' | 'note'> & { type: string };
  handleSetTarget?: () => void;
  initiativeId: string;
  survey?: Pick<SurveyActionData, '_id' | 'fragmentUniversalTrackerValues'>;
  altUnitText: string;
  currencyConfig?: CurrencyConfig;
  overrideOptions?: {
    pie?: ReactGoogleChartProps['options'],
    line?: ReactGoogleChartProps['options'],
    column?: ReactGoogleChartProps['options'],
    stacked?: ReactGoogleChartProps['options'],
    bar?: ReactGoogleChartProps['options'],
  }
}

export const MultiUtrsChartWidget = (props: ChartWidgetProps) => {
  const { utrsData, item, initiativeId, survey, handleSetTarget, readOnly, altUnitText, overrideOptions = {} } = props;
  const { subType = ChartSubType.Line, variables, calculation, unitText, note, type } = item;

  const currencyConfig = props.currencyConfig ?? EMPTY_CURRENCY_CONFIG;

  const shouldHandleNaNrAtWidget = NA_NR_CHART_SUBTYPES.includes(subType);

  if (shouldHandleNaNrAtWidget) {
    const allActualUtrvs = getActualUtrvs(utrsData);
    const { latestActualUtrvs, effectiveDate = '', period, aggregated } = getLatestActualUtrvs(utrsData);
    const naNrMessage = getNANRMessage(
      FULL_DATA_NA_NR_SUBTYPES.includes(subType) ? allActualUtrvs : latestActualUtrvs
    );

    if (naNrMessage) {
      return (
        <div className='w-100 h-100 d-flex flex-column justify-content-center'>
          <NANRMessage message={naNrMessage} />
          <TakenFromText effectiveDate={effectiveDate} period={period} aggregated={aggregated} />
        </div>
      );
    }
  }

  switch (subType) {
    case ChartSubType.SingleValue:
    case ChartSubType.SparkLine:
      // These are handled in a different component
      // Eventually we will need to support this to deal with case where there are multiple utrs
      // that are used to render a single value
      return type !== InsightDashboardItemType.Integration ? (
        <SingleValue
          readOnly={readOnly}
          utrsData={utrsData}
          variables={variables}
          calculation={calculation}
          unitText={unitText}
          note={note}
          handleSetTarget={handleSetTarget}
          hasSparkLine={subType === ChartSubType.SparkLine}
          altUnitText={altUnitText}
          currencyConfig={currencyConfig}
        />
      ) : null;
    case ChartSubType.FullPie:
    case ChartSubType.Pie:
    case ChartSubType.PieWithLegend:
      return (
        <PieChart
          getChartData={getPieChartData}
          utrsData={utrsData}
          variables={variables}
          calculation={calculation}
          subType={subType}
          overrideOptions={overrideOptions.pie}
        />
      );
    case ChartSubType.Line:
      return (
        <LineChart
          getChartData={getLineChartData}
          utrsData={utrsData}
          variables={variables}
          calculation={calculation}
          unitText={unitText}
          overrideOptions={overrideOptions.line}
          currencyConfig={currencyConfig}
        />
      );
    case ChartSubType.Column:
      return (
        <ColumnChart
          getChartData={getColumnChartData}
          utrsData={utrsData}
          variables={variables}
          calculation={calculation}
          unitText={unitText}
          overrideOptions={overrideOptions.column}
          currencyConfig={currencyConfig}
        />
      );
    case ChartSubType.Stacked:
      return (
        <ColumnChart
          getChartData={getColumnChartData}
          utrsData={utrsData}
          variables={variables}
          calculation={calculation}
          unitText={unitText}
          isStacked
          overrideOptions={overrideOptions.stacked}
          currencyConfig={currencyConfig}
        />
      );
    case ChartSubType.Bar:
      return (
        <BarChart
          getChartData={getBarChartData}
          utrsData={utrsData}
          variables={variables}
          calculation={calculation}
          note={note}
          overrideOptions={overrideOptions.bar}
          currencyConfig={currencyConfig}
        />
      );
    case ChartSubType.Table:
      // TODO: Refactor table to be subType DoubleTable of type Table
      return type !== InsightDashboardItemType.Integration ? (
        <Table
          utrsData={utrsData as HistoricalUtrs[]}
          variables={variables}
          calculation={calculation}
          initiativeId={initiativeId}
          survey={survey}
        />
      ) : null;
    default:
      return null;
  }
};
